export const PrivacyPolicyPage: React.FC = () => {
  return (
    <article className='font-mono prose dark:prose-invert lg:max-w-none leading-none'>
      <h1>Privacy Policy</h1>

      <p>Version 1.1. Effective October 2, 2026.</p>

      <h2>1. SUMMARY</h2>
      <p>YWT (YourWebTech) is a swiss registrar and cloud hosting platform operated by Cuspar GmbH, Bachtobelstrasse 38, 8123 Ebmatingen, Switzerland.</p>
      <p>
        YWT is committed to keeping your private data private. We only process your personal data when you use our products and services. Our products and services include domain registration for .ch
        and .li domains (through SWITCH), cloud hosting, and software-as-a-service (SaaS) products.
      </p>
      <p>We comply with the Swiss Federal Act on Data Protection (nFADP) for customers in Switzerland and the EU General Data Protection Regulation (GDPR) for customers in the European Union.</p>
      <p>If you have questions, please contact us at: support@ywt.ch.</p>

      <h2>2. WHAT DATA WE COLLECT</h2>
      <h3>Data you provide</h3>
      <p>We typically collect private data you voluntarily provide to us, for example:</p>
      <ul>
        <li>Account details (name, postal address, email, phone number, login credentials)</li>
        <li>Billing information (payment methods, invoices, VAT details)</li>
        <li>Domain registration data (registrant, admin, and technical contacts required by SWITCH and other registries)</li>
        <li>Content you upload or store in our SaaS/hosting services (under your responsibility)</li>
        <li>Communications with us (support requests, emails, chats, phone calls)</li>
        <li>Newsletter or marketing subscriptions (if you sign up or do not opt out)</li>
      </ul>

      <h3>Data collected automatically</h3>
      <p>We also utilize information freely offered by your web browser such as:</p>
      <ul>
        <li>Log data (IP address, time of access, login history)</li>
        <li>Device and browser details (type, version, settings)</li>
        <li>Usage data (pages visited, actions in the dashboard)</li>
        <li>DNS queries, records, and configuration changes</li>
        <li>Cookies essential for login, shopping cart, and preferences</li>
        <li>Website analytics (how pages are used: clicks, scrolling, navigation), see “Cookies and website analytics”</li>
      </ul>

      <h3>Cookies and website analytics</h3>
      <p>
        We use cookies that are essential for login sessions, the shopping cart, and site preferences. You may disable cookies in your browser, but some features may not work correctly. We do not use
        advertising cookies.
      </p>
      <p>
        To understand how our website is used and to improve it, we use Microsoft Clarity, a web analytics service of Microsoft Corporation. Clarity records how visitors interact with our pages (pages
        visited, clicks, scrolling, and technical details such as browser, device, and approximate location). What you type into forms is not recorded, and on account, checkout, and administration
        pages all text is masked before it leaves your browser.
      </p>
      <p>
        Clarity sets analytics cookies only if you accept them in the cookie banner. If you decline, Clarity runs without cookies and cannot recognize you across pages or visits. You can change your
        choice at any time under “Cookie settings” in the Legal menu at the bottom of every page. Microsoft processes this data on our behalf; see the{' '}
        <a href='https://www.microsoft.com/privacy/privacystatement' target='_blank' rel='noopener noreferrer'>
          Microsoft Privacy Statement
        </a>
        .
      </p>

      <h3>Data from third parties</h3>
      <p>We may obtain supplemental data from trusted providers (e.g. IP geolocation, fraud prevention services) to protect accounts and prevent abuse, and to better deliver products and services.</p>

      <h2>3. WHAT WE DO WITH YOUR DATA</h2>
      <p>YWT seeks to reduce the amount of private data collected from you and limits its use to situations where permission is granted.</p>
      <p>
        We pledge to use your data in order to provide , improve and optimize our products and services, to perform contracts, record-keeping or regulatory obligations, or as required by a law
        enforcement agency, lawful order, or as required or permitted for other lawful purposes.
      </p>
      <p>Here are a few examples of how we use your data:</p>
      <ul>
        <li>Register and manage your domains</li>
        <li>Provide SaaS and hosting services</li>
        <li>Process payments and issue invoices</li>
        <li>Verify your identity and prevent fraud</li>
        <li>Comply with SWITCH and registry policies</li>
        <li>Communicate with you about our services</li>
      </ul>

      <h3>Sharing with trusted third parties</h3>
      <p>
        We do not sell, trade or otherwise transfer to outside parties your personally identifiable information unless we provide you with advance notice, except as specifically described in this
        Privacy Policy.
      </p>
      <p>We share your data only as necessary to provide services or comply with law, for instance, for:</p>
      <ul>
        <li>Domain registries (e.g. SWITCH for .ch and .li domains)</li>
        <li>Payment processors (Stripe)</li>
        <li>Website analytics (Microsoft Clarity)</li>
        <li>Authentication providers (Google Cloud)</li>
        <li>Cloud hosting infrastructure providers (Google Cloud, Hetzner)</li>
      </ul>
      <p>
        You can always review, update, or change your personal information from within your account. YWT will provide you with information about where we hold or process on behalf of a third party,
        any of your personal information, upon your request.
      </p>

      <h2>4. DATA STORAGE AND SECURITY</h2>
      <p>Personal data collected by YWT from you is stored in our data center in Switzerland and EU.</p>
      <p>
        To safely protect your data, we apply technical and organizational measures, including encryption, secure servers, and restricted staff access. We utilize physical, electronic, and managerial
        procedures to help safeguard, prevent unauthorized access to, maintain data security of, and correctly use your information.
      </p>
      <p>
        Despite all our reasonable efforts to protect your personal information, please understand that no system is ensured or warranted to be 100% secure and that all personal information your
        disclose to us is at your own risk.
      </p>
      <p>Should you become aware or have reason to believe that there has been unauthorized use of your personal data in connection with YWT, please contact us immediately.</p>

      <h3>Retention period</h3>
      <p>The duration your personal data stays with YWT varies based on the data type:</p>
      <ul>
        <li>Domain registration data: as long as the domain is active</li>
        <li>Billing and accounting records: 10 years (required under Swiss law)</li>
        <li>Service logs: normally up to 90 days, longer if needed for fraud</li>
        <li>Account data: until you delete your account, unless retention is required by law</li>
      </ul>

      <h3>Manage and delete your personal data</h3>
      <p>You can view, edit your account data directly in your account settings.</p>
      <p>
        If you wish to have your personal account deleted, please contact us at: support@ywt.ch. Your request to delete your personal data will be responded within 7 days. Deleting your account may
        require transferring or deleting any active domains or services.
      </p>

      <h2>5. UPDATE TO THIS POLICY</h2>
      <p>YWT may update this Privacy Policy from time to time. The latest version will always be published on our website. We will notify you by email if significant changes are made.</p>

      <h2>6. CONTACT</h2>
      <p>If you have any questions about our Privacy Policy, our customer personal data practices, our products and services, or to report a privacy violation, contact us at: support@ywt.ch, or</p>

      <p>Cuspar GmbH, Bachtobelstrasse 38, 8123 Ebmatingen, Switzerland.</p>
      <p>We will respond to all requests and inquiries within 30 days.</p>
    </article>
  );
};
